/**
 * LVGL-conformant outline geometry for the shared main-object rect.
 *
 * Like borderGeometry.ts / shadowGeometry.ts this is a pure module — no Konva,
 * no canvas state, no widget knowledge — so it is unit testable in the plain
 * `node` environment. CustomRect is its only renderer, and every widget reaches
 * that renderer through the single `this.rect` created in ObjectGroup, so there
 * is no per-widget outline code.
 *
 * The model is taken verbatim from LVGL 7.11's lv_draw_rect.c `draw_outline()`
 * plus `draw_full_border(..., radius_is_in = true)` — the exact files openHASP
 * vendors (openhasp-next/lib/lvgl-7.11.0), re-read against upstream
 * release/v7 before this was written:
 *
 *     pad   = (outline_pad == 0) ? -1 : outline_pad          // note: -1, not 0
 *     width = (outline_pad == 0) ? outline_width + 1 : outline_width
 *     inner = coords grown by pad on every side
 *     outer = inner  grown by width on every side
 *     rin   = min(radius, min(inner_w, inner_h) / 2)         // ORIGINAL radius
 *     rout  = rin + width
 *
 * Three consequences worth spelling out, because each one is easy to get wrong:
 *
 *   1. The outline is ALWAYS a closed ring around the whole object. It is
 *      completely independent of border_side — there is no outline side mask in
 *      LVGL, and none here.
 *
 *   2. The radius clamp uses the ORIGINAL resolved radius against the PADDED
 *      INNER box, never the object's already-clamped radius. That is what makes
 *      a 100x100 object with radius 50 render as a circle whose outline is still
 *      a rounded square, while radius 100 (or 999) rounds both to circles.
 *
 *   3. `outline_pad == 0` is a special case in firmware, not a rounding
 *      artifact: LVGL pulls the inner edge 1px INTO the object and widens the
 *      ring by 1px, so the outline overlaps the background instead of leaving an
 *      anti-aliasing seam (upstream commit 681cf0dc5). The outward extent is
 *      still exactly `outline_width`, so `overflow == pad + width` holds either
 *      way.
 *
 * Rendered as ONE closed canvas stroke rather than the even-odd ring fill the
 * border needs: a single stroke of a single closed path paints every pixel
 * exactly once, so a translucent outline stays uniform at the corners. (The
 * border's ring fill exists to support border_side, which the outline has no
 * equivalent of.) The stroke is described by the centerline of that ring:
 *
 *     path   = inner grown by width / 2
 *     radius = rin + width / 2 = (rin + rout) / 2
 */

import { PathSink, appendRoundedRect, effectiveRadius } from './borderGeometry';

/** LVGL's LV_OPA_COVER — the value an unset outline_opa resolves to. */
export const OUTLINE_OPA_COVER = 255;

/** LVGL's LV_OPA_MIN — `outline_opa <= LV_OPA_MIN` draws nothing (draw_outline's first early-out). */
export const OUTLINE_OPA_MIN = 2;

/**
 * The colour an unset outline_color resolves to. LVGL's
 * `_lv_obj_get_style_color()` returns LV_COLOR_BLACK for every unset colour
 * property except bg_color/bg_grad_color, and the openHASP theme deliberately
 * declares no outline properties at all (lv_theme_hasp.c) — so `outline_width: 4`
 * alone draws a black outline on the device, and must do the same here.
 */
export const OUTLINE_COLOR_DEFAULT = '#000000';

export interface OutlineRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface OutlineInput {
    /** The object's box, always local (0,0)-anchored. */
    width: number;
    height: number;
    /** The object's ORIGINAL resolved radius — never the value already clamped to the object. */
    radius?: number | null;
    outlineWidth?: number | null;
    outlinePad?: number | null;
    /** 0-255; unset resolves to LV_OPA_COVER, matching lv_draw_rect_dsc_init(). */
    opa?: number | null;
}

export interface OutlineGeometry {
    /** False when LVGL would draw nothing at all. */
    visible: boolean;
    /** Centerline rectangle for the single stroke, in object-local coordinates. */
    path: OutlineRect;
    /** Centerline corner radius — (rin + rout) / 2. */
    radius: number;
    /** Stroke thickness — LVGL's effective outline width (see the pad == 0 case). */
    strokeWidth: number;
    /** LVGL's `rin`: the corner radius at the ring's inner edge. */
    radiusInner: number;
    /** LVGL's `rout`: the corner radius at the ring's outer edge. */
    radiusOuter: number;
    /** 0-255. */
    opa: number;
    /** How far outside the object's box the outline reaches, on every side. */
    overflow: number;
}

/**
 * Coerce a possibly-missing / string / NaN value to a finite number. `null` and
 * `''` count as missing, not as 0 — an unset property must fall back to LVGL's
 * default (which for outline_opa is LV_OPA_COVER, not transparent).
 */
function num(value: unknown, fallback: number): number {
    if (value === null || value === undefined || value === '') return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/**
 * Resolve the outline geometry for one main object. Pure — same inputs always
 * produce the same output, and no rounding to device pixels happens here (the
 * stage transform may place the shape on fractional coordinates at odd zoom).
 */
export function resolveOutlineGeometry(input: OutlineInput): OutlineGeometry {
    const width = Math.max(0, num(input.width, 0));
    const height = Math.max(0, num(input.height, 0));
    // outline_width/outline_pad are lv_style_int_t in firmware and non-negative
    // by schema; round so a stray fractional value cannot shift the pad == 0 case.
    const rawWidth = Math.max(0, Math.round(num(input.outlineWidth, 0)));
    const rawPad = Math.max(0, Math.round(num(input.outlinePad, 0)));
    const opa = clamp(Math.round(num(input.opa, OUTLINE_OPA_COVER)), 0, 255);

    // LVGL's zero-pad overlap: the inner edge moves 1px INTO the object and the
    // ring grows 1px, keeping the outward extent at exactly outline_width.
    // Gated on a non-zero width because firmware returns before this on width 0.
    const overlap = rawPad === 0 && rawWidth > 0;
    const pad = overlap ? -1 : rawPad;
    const strokeWidth = overlap ? rawWidth + 1 : rawWidth;

    const innerWidth = width + 2 * pad;
    const innerHeight = height + 2 * pad;

    const radiusInner = effectiveRadius(innerWidth, innerHeight, input.radius);
    const radiusOuter = radiusInner + strokeWidth;

    const half = strokeWidth / 2;
    const path: OutlineRect = {
        x: -pad - half,
        y: -pad - half,
        width: innerWidth + strokeWidth,
        height: innerHeight + strokeWidth,
    };

    // LVGL's early-outs, verbatim (lv_draw_rect.c draw_outline):
    //   outline_opa <= LV_OPA_MIN -> nothing
    //   outline_width == 0        -> nothing
    // plus the degenerate boxes that cannot produce a ring at all.
    const visible =
        rawWidth > 0 &&
        opa > OUTLINE_OPA_MIN &&
        width > 0 &&
        height > 0 &&
        innerWidth > 0 &&
        innerHeight > 0;

    return {
        visible,
        path,
        radius: radiusInner + half,
        strokeWidth,
        radiusInner,
        radiusOuter,
        opa,
        overflow: pad + strokeWidth,
    };
}

/**
 * Append the outline's single closed subpath — the ring's centerline. The
 * caller strokes it once with `geom.strokeWidth`; one stroke of one closed path
 * is what keeps a translucent outline uniform at the corners.
 *
 * Reuses borderGeometry's path builder so the outline's corners are built by
 * exactly the same code as the background's and the border's.
 */
export function appendOutlinePath(sink: PathSink, geom: OutlineGeometry): void {
    appendRoundedRect(sink, geom.path.x, geom.path.y, geom.path.width, geom.path.height, geom.radius);
    sink.closePath();
}
